import { ReactNode } from "react";
import { Box, FormControlLabel, Switch, SwitchProps, SxProps, Theme } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";

type ConsentControlledSwitchProps = {
    checked: boolean;
    disabled?: boolean;
    inputProps?: SwitchProps["inputProps"];
    label?: ReactNode;
    onChange: (checked: boolean) => void;
    sx?: SxProps<Theme>;
};

const StyledSwitch = styled(Switch)(({ theme }) => ({
    width: 44,
    height: 28,
    padding: 0,
    display: "flex",
    "& .MuiSwitch-switchBase": {
        padding: 4,
        transitionDuration: "180ms",
        "&.Mui-checked": {
            transform: "translateX(16px)",
            color: theme.palette.common.white,
            "& + .MuiSwitch-track": {
                backgroundColor: theme.palette.success.main,
                opacity: 1,
            },
            "&.Mui-disabled + .MuiSwitch-track": {
                opacity: 0.45,
            },
        },
        "&.Mui-focusVisible .MuiSwitch-thumb": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
        },
        "&.Mui-disabled .MuiSwitch-thumb": {
            color: theme.palette.grey[300],
        },
        "&.Mui-disabled + .MuiSwitch-track": {
            opacity: 0.35,
        },
    },
    "& .MuiSwitch-thumb": {
        boxShadow: "none",
        width: 20,
        height: 20,
    },
    "& .MuiSwitch-track": {
        borderRadius: 14,
        opacity: 1,
        backgroundColor: alpha(theme.palette.common.black, 0.24),
        boxSizing: "border-box",
    },
}));

export const ConsentControlledSwitch = ({
    checked,
    disabled = false,
    inputProps,
    label,
    onChange,
    sx,
}: ConsentControlledSwitchProps) => {
    const sxArray =
        Array.isArray(sx) ? sx
        : sx ? [sx]
        : [];
    const switchControl = (
        <StyledSwitch
            checked={checked}
            disabled={disabled}
            inputProps={inputProps}
            onChange={(event) => onChange(event.target.checked)}
        />
    );

    if (label) {
        return (
            <FormControlLabel
                control={switchControl}
                label={label}
                sx={[
                    {
                        alignItems: "center",
                        gap: 1,
                        m: 0,
                        minHeight: 44,
                    },
                    ...sxArray,
                ]}
            />
        );
    }

    return (
        <Box
            sx={[
                {
                    alignItems: "center",
                    display: "inline-flex",
                    justifyContent: "center",
                    minHeight: 44,
                    minWidth: 44,
                },
                ...sxArray,
            ]}
        >
            {switchControl}
        </Box>
    );
};
